import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import { ResourceDetailsResponse } from '../../../../../primary-record-types/apiTypes/medicalDataService';
import { PrimaryRecordResource } from '../../../../../primary-record-types/MedicalDataTypes';
import {
    allergyTypeMessages,
    conditionStatusMessages,
    medicationFrequency,
    reactionTypeMessages,
    visitDurationMessages,
    getVisitTypeDisplay,
} from '../../../../../utils/sharedMessages';
import { resourceTypeGuards } from '../../../../../utils/medicalDataUtils';
import getDateString from '../../../../../utils/getDateString';
import addressToString from '../../../../../utils/addressToString';
import LabelValueDisplay, { LabelValueItem } from '../../../../v2BaseComponents/LabelValueDisplay';
import { toTitleCase } from '../../../../../utils/toTitleCase';

const intl = getIntl('resource-details-list', {
    startedOn: 'Started: {date}',
    details: 'Details',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    notes: 'Additional Information',
    reactionDescription: 'Reaction Description',
    reactionType: 'Reaction Type',
    edit: 'Edit {category}',
    delete: 'Delete {category}',
    fromIntegration: 'From {integration}',
    fromPatientPortal: 'From Patient Portal',
    fromUser: 'Added by {name}',
    allergyType: 'Type',
    medicationType: 'Medication Type',
    genericName: 'Generic Name or Active Ingredient',
    strength: 'Strength',
    form: 'Form',
    route: 'Route',
    quantity: 'Quantity',
    frequency: 'Frequency',
    whenToTake: 'When to Take',
    isActive: 'Active?',
    numberOfRefills: 'Number of Refills',
    lastRefill: 'Last Refill',
    yes: 'Yes',
    no: 'No',
    providerName: 'Provider Name',
    visitType: 'Visit Type',
    reason: 'Reason',
    duration: 'Duration',
    address: 'Address',
    date: 'Date',
    results: 'Results',
    vaccine: 'Vaccine',
    asNeeded: 'Taken as Needed?',
});

export type DetailsListOwnProps = {
    resource: ResourceDetailsResponse<PrimaryRecordResource>;
};

export type DetailsListProps = DetailsListOwnProps;

const DetailsList: React.FC<DetailsListProps> = ({ resource }) => {
    // @ts-expect-error TS2366
    const getLabelValueItems = (): LabelValueItem[] => {
        if (resourceTypeGuards.isCondition(resource.resource)) {
            return [
                {
                    label: intl('status'),
                    value: resource.resource.status ? conditionStatusMessages(resource.resource.status) : '',
                },
                {
                    label: intl('startDate'),

                    value: getDateString(resource.resource.startDateTime, 'date'),
                },
                {
                    label: intl('endDate'),

                    value: getDateString(resource.resource.endDateTime, 'date'),
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isAllergy(resource.resource)) {
            return [
                {
                    label: intl('status'),
                    value: resource.resource.status ? conditionStatusMessages(resource.resource.status) : '',
                },
                {
                    label: intl('startDate'),

                    value: getDateString(resource.resource.startDateTime, 'date'),
                },
                {
                    label: intl('endDate'),

                    value: getDateString(resource.resource.endDateTime, 'date'),
                },
                {
                    label: intl('allergyType'),
                    value: resource.resource.allergyType ? allergyTypeMessages(resource.resource.allergyType) : '',
                },
                {
                    label: intl('reactionType'),
                    value: resource.resource.reactionType ? reactionTypeMessages(resource.resource.reactionType) : '',
                },
                {
                    label: intl('reactionDescription'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isMedication(resource.resource)) {
            return [
                {
                    label: intl('medicationType'),
                    value: toTitleCase(resource.resource.name, { ignoreAllCaps: true }),
                },
                {
                    label: intl('genericName'),
                    value: resource.resource.genericNameOrActiveIngredient,
                },
                {
                    label: intl('strength'),
                    value: resource.resource.strength,
                },
                {
                    label: intl('form'),
                    value: resource.resource.form,
                },
                {
                    label: intl('route'),
                    value: resource.resource.route,
                },
                {
                    label: intl('quantity'),
                    value: resource.resource.quantity,
                },
                {
                    label: intl('asNeeded'),
                    value: resource.resource.asNeeded ? intl('yes') : intl('no'),
                },
                {
                    label: intl('frequency'),
                    value: resource.resource.frequency ? medicationFrequency(resource.resource.frequency) : null,
                },
                {
                    label: intl('whenToTake'),
                    value: resource.resource.whenToTake,
                },
                {
                    label: intl('numberOfRefills'),
                    value: resource.resource.numberOfRefills,
                },
                {
                    label: intl('isActive'),
                    value: resource.resource.isActive ? intl('yes') : intl('no'),
                },
                {
                    label: intl('startDate'),
                    value: getDateString(resource.resource.startDateTime, 'date'),
                },
                {
                    label: intl('endDate'),
                    value: getDateString(resource.resource.endDateTime, 'date'),
                },
                {
                    label: intl('providerName'),
                    value: resource.resource.providerName,
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isVisit(resource.resource)) {
            return [
                {
                    label: intl('providerName'),
                    value: resource.resource.providerName,
                },
                {
                    label: intl('visitType'),
                    value: getVisitTypeDisplay(resource.resource) || '',
                },
                {
                    label: intl('reason'),
                    value: resource.resource.reason,
                },
                {
                    label: intl('duration'),
                    value: resource.resource.duration ? visitDurationMessages(resource.resource.duration) : '',
                },
                {
                    label: intl('startDate'),

                    value: getDateString(resource.resource.startDateTime, 'date-time'),
                },
                {
                    label: intl('endDate'),

                    value: getDateString(resource.resource.endDateTime, 'date-time'),
                },
                {
                    label: intl('address'),
                    value: addressToString(resource.resource),
                    linkType: 'address',
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isProcedure(resource.resource)) {
            return [
                {
                    label: intl('providerName'),
                    value: resource.resource.providerName,
                },
                {
                    label: intl('date'),
                    value: getDateString(resource.resource.dateTime, 'date-time'),
                },
                {
                    label: intl('address'),
                    value: addressToString(resource.resource),
                    linkType: 'address',
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isImaging(resource.resource)) {
            return [
                {
                    label: intl('results'),
                    value: resource.resource.results,
                },
                {
                    label: intl('date'),
                    value: getDateString(resource.resource.dateTime, 'date-time'),
                },
                {
                    label: intl('address'),
                    value: addressToString(resource.resource),
                    linkType: 'address',
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        } else if (resourceTypeGuards.isVaccination(resource.resource)) {
            return [
                {
                    label: intl('date'),
                    value: getDateString(resource.resource.dateTime, 'date'),
                },
                {
                    label: intl('vaccine'),
                    value: resource.resource.vaccineType.display,
                },
                {
                    label: intl('address'),
                    value: addressToString(resource.resource),
                    linkType: 'address',
                },
                {
                    label: intl('notes'),
                    value: resource.resource.notes,
                },
            ];
        }
    };

    return <LabelValueDisplay items={getLabelValueItems()} omitEmptyValues />;
};

export default React.memo(DetailsList);
